import React, { useMemo, useState } from 'react';
import { Checkbox, Input } from 'antd';
import { CiSearch } from 'react-icons/ci';
import type { PermissionPrincipal } from '../../../../../shared/components/PermissionsPicker';
import styles from './checklistPanel.module.scss';

export interface ChecklistPanelProps {
  items: PermissionPrincipal[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  searchPlaceholder: string;
  emptyMessage: string;
}

interface ItemRowProps {
  item: PermissionPrincipal;
  isSelected: boolean;
  onToggle: (id: string) => void;
}

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

const ItemRow = ({ item, isSelected, onToggle }: ItemRowProps) => {
  const handleClick = () => onToggle(item.id);

  return (
    <div className={styles.item} onClick={handleClick}>
      <span className={styles.label}>{item.name}</span>
      <Checkbox className="custom-checkbox" checked={isSelected} onChange={handleClick} onClick={stopPropagation} />
    </div>
  );
};

// Searchable, multi-select list backing the Add Users and Map Permissions sections.
const ChecklistPanel = ({ items, selectedIds, onChange, searchPlaceholder, emptyMessage }: ChecklistPanelProps) => {
  const [search, setSearch] = useState('');
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filteredItems = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return items;
    return items.filter((item) => item.name.toLowerCase().includes(normalized));
  }, [search, items]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value);

  const handleToggle = (id: string) => {
    onChange(selectedSet.has(id) ? selectedIds.filter((selected) => selected !== id) : [...selectedIds, id]);
  };

  return (
    <div className={styles.panel}>
      <Input
        className={styles.search}
        value={search}
        onChange={handleSearchChange}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        suffix={<CiSearch className={styles.searchIcon} />}
      />
      <div className={styles.list}>
        {filteredItems.length === 0 ? (
          <p className={styles.empty}>{emptyMessage}</p>
        ) : (
          filteredItems.map((item) => <ItemRow key={item.id} item={item} isSelected={selectedSet.has(item.id)} onToggle={handleToggle} />)
        )}
      </div>
    </div>
  );
};

export default ChecklistPanel;
